import React, { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import type { ThemeMetadata } from '../config/themes'

const STORAGE_KEY = 'htd-theme-switcher-float'
const DRAG_THRESHOLD = 6
const PANEL_WIDTH = 240
const DOCK_TAB_WIDTH = 34
const DOCK_TAB_HEIGHT = 96

type DockSide = 'left' | 'right'

interface FloatState {
  x: number
  y: number
  docked: boolean
  dockSide: DockSide
}

interface ThemeSwitcherFloatProps {
  themeId: string
  themes: ThemeMetadata[]
  tenantId?: string | null
  onSwitch: (themeId: string) => void
  onReset: () => void
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

function defaultState(): FloatState {
  const width = typeof window !== 'undefined' ? window.innerWidth : 1024
  return {
    x: Math.max(8, width - PANEL_WIDTH - 12),
    y: 12,
    docked: false,
    dockSide: 'right',
  }
}

const ThemeSwitcherFloat: React.FC<ThemeSwitcherFloatProps> = ({
  themeId,
  themes,
  tenantId,
  onSwitch,
  onReset,
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<FloatState>(() => defaultState())
  const dragRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    pointerId: -1,
  })

  const persist = useCallback((next: FloatState) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // ignore quota errors
    }
  }, [])

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw) as Partial<FloatState>
      setState((prev) => ({
        x: typeof parsed.x === 'number' ? parsed.x : prev.x,
        y: typeof parsed.y === 'number' ? parsed.y : prev.y,
        docked: Boolean(parsed.docked),
        dockSide: parsed.dockSide === 'left' ? 'left' : 'right',
      }))
    } catch {
      // keep defaults
    }
  }, [])

  const clampToViewport = useCallback((x: number, y: number): { x: number; y: number } => {
    const panel = panelRef.current
    const w = panel?.offsetWidth || PANEL_WIDTH
    const h = panel?.offsetHeight || 160
    const maxX = Math.max(8, window.innerWidth - w - 8)
    const maxY = Math.max(8, window.innerHeight - h - 8)
    return { x: clamp(x, 8, maxX), y: clamp(y, 8, maxY) }
  }, [])

  useEffect(() => {
    const onResize = () => {
      setState((prev) => {
        if (prev.docked) {
          return { ...prev, y: clamp(prev.y, 8, Math.max(8, window.innerHeight - DOCK_TAB_HEIGHT - 8)) }
        }
        const next = clampToViewport(prev.x, prev.y)
        return { ...prev, ...next }
      })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [clampToViewport])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (state.docked) return
    dragRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
      originX: state.x,
      originY: state.y,
      pointerId: event.pointerId,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }, [state.docked, state.x, state.y])

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const d = dragRef.current
    if (!d.active || d.pointerId !== event.pointerId) return
    const dx = event.clientX - d.startX
    const dy = event.clientY - d.startY
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) d.moved = true
    const next = clampToViewport(d.originX + dx, d.originY + dy)
    setState((prev) => ({ ...prev, ...next }))
  }, [clampToViewport])

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const d = dragRef.current
    if (!d.active || d.pointerId !== event.pointerId) return
    d.active = false
    event.currentTarget.releasePointerCapture(event.pointerId)
    setState((prev) => {
      const next = { ...prev, ...clampToViewport(prev.x, prev.y) }
      persist(next)
      return next
    })
  }, [clampToViewport, persist])

  const dock = useCallback(() => {
    setState((prev) => {
      const panel = panelRef.current
      const w = panel?.offsetWidth || PANEL_WIDTH
      const side: DockSide = prev.x + w / 2 >= window.innerWidth / 2 ? 'right' : 'left'
      const y = clamp(prev.y, 8, Math.max(8, window.innerHeight - DOCK_TAB_HEIGHT - 8))
      const next = { ...prev, docked: true, dockSide: side, y }
      persist(next)
      return next
    })
  }, [persist])

  const undock = useCallback(() => {
    setState((prev) => {
      const next = { ...prev, docked: false }
      const clamped = clampToViewport(next.x, next.y)
      const merged = { ...next, ...clamped }
      persist(merged)
      return merged
    })
  }, [clampToViewport, persist])

  if (state.docked) {
    const tabStyle: React.CSSProperties = {
      position: 'fixed',
      top: `${state.y}px`,
      width: `${DOCK_TAB_WIDTH}px`,
      height: `${DOCK_TAB_HEIGHT}px`,
      zIndex: 10050,
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      color: '#fff',
      background: 'linear-gradient(180deg, var(--htd-primary), var(--htd-secondary))',
      boxShadow: '0 8px 20px rgba(15, 23, 42, 0.32)',
      ...(state.dockSide === 'right'
        ? { right: 0, borderRadius: '16px 0 0 16px' }
        : { left: 0, borderRadius: '0 16px 16px 0' }),
    }
    return (
      <button type="button" style={tabStyle} onClick={undock} aria-label="แสดงตัวเลือกธีม">
        <span style={{ fontSize: 16, lineHeight: 1 }}>🎨</span>
        <span style={{ fontSize: 11, lineHeight: 1.1, writingMode: 'vertical-rl', letterSpacing: 1 }}>ธีม</span>
        <span style={{ fontSize: 13, lineHeight: 1 }}>{state.dockSide === 'right' ? '‹' : '›'}</span>
      </button>
    )
  }

  return (
    <div
      ref={panelRef}
      className="htd-theme-switcher"
      aria-label="HTD theme switcher"
      style={{ left: `${state.x}px`, top: `${state.y}px`, right: 'auto' }}
    >
      <div
        className="htd-theme-switcher__handle"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        role="button"
        tabIndex={0}
        aria-label="ลากเพื่อย้ายตัวเลือกธีม"
      >
        <span className="htd-theme-switcher__grip" aria-hidden="true">⠿</span>
        <small>HTD Theme</small>
        <button
          type="button"
          className="htd-theme-switcher__collapse"
          onClick={dock}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label="ซ่อนไว้ขอบจอ"
          title="ซ่อนไว้ขอบจอ"
        >
          ⎯
        </button>
      </div>
      <select
        value={themeId}
        onChange={(event) => onSwitch(event.target.value)}
        aria-label="เลือกสีธีม"
      >
        {themes.map((theme) => (
          <option key={theme.id} value={theme.id}>
            {theme.displayName}
          </option>
        ))}
      </select>
      <button type="button" className="htd-theme-switcher__reset" onClick={onReset}>
        ตาม Tenant
      </button>
      {tenantId ? <span>{tenantId}</span> : null}
    </div>
  )
}

export default ThemeSwitcherFloat
